import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import './DragonSelectionScreen.css';

const DragonSelectionScreen = ({ onDragonSelected }) => {
  const { t } = useLocalization();
  const [dragonTypes, setDragonTypes] = useState([]);
  const [selectedDragon, setSelectedDragon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAvailableDragons();
  }, []);

  const fetchAvailableDragons = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/dragons/available-eggs');
      
      if (response.status === 200) {
        setDragonTypes(response.data.dragon_types);
      } else {
        setError(t('dragon.failedToLoadDragons'));
      }
    } catch (error) {
      console.error('Error fetching dragons:', error);
      setError(t('dragon.failedToConnect'));
    } finally {
      setLoading(false);
    }
  };

  const selectDragon = async (dragonId) => {
    try {
      setSelecting(true);
      setError(null);
      
      const response = await api.post('/api/dragons/select', {
        dragon_name: dragonId
      });
      
      if (response.status === 200) {
        // Dragon selected successfully
        if (onDragonSelected) {
          onDragonSelected(dragonId);
        }
      } else {
        setError(response.data.error || t('dragon.failedToSelectDragon'));
      }
    } catch (error) {
      console.error('Error selecting dragon:', error);
      if (error.response) {
        setError(error.response.data.error || t('dragon.failedToSelectDragon'));
      } else {
        setError(t('dragon.failedToConnect'));
      }
    } finally {
      setSelecting(false);
    }
  };

  const confirmSelection = () => {
    if (selectedDragon) {
      selectDragon(selectedDragon.id);
    }
  };

  if (loading) {
    return (
      <div className="dragon-selection-screen">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dragon-selection-screen">
        <div className="error-container">
          <h2>{t('dragon.error')}</h2>
          <p>{error}</p>
          <button onClick={fetchAvailableDragons}>
            {t('dragon.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dragon-selection-screen">
      <div className="selection-header">
        <h1>{t('dragonSelection.title')}</h1>
        <p>{t('dragonSelection.subtitle')}</p>
      </div>

      <div className="dragon-grid">
        {dragonTypes.map((dragon) => (
          <div 
            key={dragon.id}
            className={`dragon-egg-card ${selectedDragon?.id === dragon.id ? 'selected' : ''}`}
            onClick={() => setSelectedDragon(dragon)}
          >
            <div className="dragon-egg-image">
              <img src={getApiImageUrl(dragon.image_url)} alt={dragon.name} />
            </div>
            <div className="dragon-name">{dragon.name}</div>
          </div>
        ))}
      </div>

      {selectedDragon && (
        <div className="selection-confirmation">
          <div className="selected-dragon-info">
            <h3>{t('dragonSelection.selected')}: {selectedDragon.name}</h3>
            <p>{t('dragonSelection.confirmMessage')}</p>
          </div>
          <button 
            className="confirm-button"
            onClick={confirmSelection}
            disabled={selecting}
          >
            {selecting ? t('common.loading') : t('dragonSelection.confirm')}
          </button>
        </div>
      )}
    </div>
  );
};

export default DragonSelectionScreen;